/**
 * @file XeroPoInvoiceModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-19
 * @modified 2026-11-03
 * @description Pricing & Costing — invoice lines of POs made in Xero, per line
 *              (Cam, 19 Oct 2026: "price and invoice PER LINE of such POs, and
 *              choose to invoice either the customer from the PO's delivery
 *              address (default) OR any other customer (searchable drop
 *              down)"). Each line with its customer (the PO's, else chosen
 *              here), quantity and confirmed price; lines that cannot go yet
 *              say why and are left out. What it will raise is shown first —
 *              one invoice per customer per delivery day, dated that delivery date (the
 *              first open day where Xero has locked it; 3 Nov 2026 — it was
 *              today) — and Raise asks once, then the prior-month question.
 *              POST /api/pricing-costing/xero-po-lines/invoice.
 */

import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import usePriorMonthConfirm from '../hooks/usePriorMonthConfirm';
import { API_URL } from '../config';
import { money } from '../utils/pricingCosting';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import { formatDate } from '../utils/dateFormatters';
import './PricingCosting.css';
import './XeroPoInvoiceModal.css';

const qtyText = (r) => {
  if (!r.unit) return formatQuantity(r.qty);
  if (r.unit === 'T') return `${formatQuantity(r.qty)} T`;
  return `${formatCount(r.qty)} × ${r.unit}`;
};

function XeroPoInvoiceModal({ rows, customers, onClose }) {
  const [chosen, setChosen] = useState(() => Object.fromEntries(rows.map(r => [r.key, r.invoice_to?.id ? String(r.invoice_to.id) : ''])));
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const nameOf = (id) => customers.find(c => String(c.id) === String(id))?.company_name || '';
  const options = useMemo(() => customers.map(c => ({
    value: String(c.id), label: `${c.company_name}${c.account_no ? ` (${c.account_no})` : ''}`,
    searchText: `${c.company_name} ${c.account_no || ''}`
  })), [customers]);

  // What each line is: going, or why not.
  const lines = rows.map(r => {
    const price = r.sale?.confirmed?.value ?? null;
    const block = r.invoice_block || (!chosen[r.key] ? 'Choose the customer to invoice' : null);
    return { r, price, block };
  });
  const going = lines.filter(l => !l.block);
  const groups = useMemo(() => {
    const m = new Map();
    going.forEach(l => {
      const k = chosen[l.r.key];
      if (!m.has(k)) m.set(k, { id: k, name: nameOf(k), lines: [], net: 0 });
      const g = m.get(k);
      g.lines.push(l);
      g.net += (Number(l.price) || 0) * (Number(l.r.qty) || 0);
    });
    return [...m.values()];
  }, [going.map(l => `${l.r.key}:${chosen[l.r.key]}`).join('|'), customers]); // eslint-disable-line react-hooks/exhaustive-deps

  const priorMonth = usePriorMonthConfirm();
  const raise = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = await priorMonth.run((extra) => fetch(`${API_URL}/api/pricing-costing/xero-po-lines/invoice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ lines: going.map(l => ({ key: l.r.key, accountId: Number(chosen[l.r.key]) })), ...extra })
      }));
      if (out.declined) return;
      const body = out.data;
      if (!out.res.ok || !body?.success) throw new Error(body?.error || 'Nothing was invoiced');
      setOutcome(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={() => onClose(!!outcome)}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">{outcome ? 'Done' : 'Cancel'}</span>
        </button>
      </div>
      {!outcome && (
        <div>
          <button type="button" className="btn-slideout" onClick={() => setAsking(true)} disabled={busy || !groups.length}
            title={groups.length ? '' : 'No line can be invoiced yet'}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">request_quote</span></div>
            <span className="btn-label">{busy ? 'Invoicing…' : `Invoice ${groups.length} customer${groups.length === 1 ? '' : 's'}`}</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal isOpen onClose={() => onClose(!!outcome)} title="Invoice Xero PO lines" size="xl" className="xero-po-invoice" footer={footer}>
      {/* Padded inside the scroller, never on it: the sticky header must sit at
          its top (XeroPoInvoiceModal.css). */}
      <div className="xpi-body">
        <InvoiceBody
          lines={lines} chosen={chosen} setChosen={setChosen} options={options} groups={groups}
          error={error} outcome={outcome} busy={busy}
        />
      </div>
      <ConfirmDialog
        isOpen={asking}
        title="Raise invoices"
        type="info"
        message={`Raise invoices for ${groups.length === 1 ? 'one customer' : `${groups.length} customers`} — one per delivery day, dated its delivery date — at the confirmed prices: ${groups.map(g => `${g.name} (${g.lines.length} line${g.lines.length === 1 ? '' : 's'}, ${money(g.net)} + VAT)`).join('; ')}? Each goes to Xero as an approved invoice.`}
        confirmText="Raise"
        confirmIcon="request_quote"
        onConfirm={() => { setAsking(false); raise(); }}
        onCancel={() => setAsking(false)}
      />
      {priorMonth.dialog}
    </Modal>
  );
}

/** The window's body; mounts with the footer, so it measures the slide-out buttons. */
function InvoiceBody({ lines, chosen, setChosen, options, groups, error, outcome, busy }) {
  useSlideOutButton([groups.length, busy, !!outcome]);
  if (outcome) {
    const bad = (outcome.results || []).filter(r => r.outcome !== 'invoiced');
    return (
      <div className="xpi-outcome">
        {(outcome.invoices || []).map(i => (
          <p key={i.id} className="pc-flag info"><span className="material-symbols-rounded">check_circle</span>
            {i.invoice_no} raised for {i.customer} ({i.lines} line{i.lines === 1 ? '' : 's'}){i.xeroWarning ? ` — ${i.xeroWarning}` : ''}</p>
        ))}
        {bad.map((b, i) => <p key={i} className="pc-flag err"><span className="material-symbols-rounded">error</span>{b.message}</p>)}
        {!(outcome.invoices || []).length && !bad.length && <p className="pc-muted">Nothing was invoiced.</p>}
      </div>
    );
  }
  return (
    <>
      {error && <p className="pc-flag err"><span className="material-symbols-rounded">error</span>{error}</p>}
      <p className="pc-muted">
        Each line is invoiced on its own, to the customer the PO names (its customer, else its delivery postcode) or the one chosen here.
        Lines to one customer delivered on one day go on one invoice, dated that delivery date, with their PO numbers as its reference.
      </p>
      <table className="data-table xpi-table">
        <thead>
          <tr><th>PO</th><th>Product</th><th>Quantity</th><th className="num">Price</th><th>Invoice to</th></tr>
        </thead>
        <tbody>
          {lines.map(({ r, price, block }) => (
            <tr key={r.key} className={block && r.invoice_block ? 'pc-dim' : ''}>
              <td>{r.refs[0]?.label}<div className="pc-muted">{formatDate(r.delivery_date)}</div></td>
              <td>{r.item?.name || r.item?.code}<div className="pc-muted">{r.item?.code}</div></td>
              <td>{qtyText(r)}</td>
              <td className="num">{price !== null ? `${money(price)}${r.per || ''}` : '—'}</td>
              <td className="xpi-customer">
                <SearchableDropdown
                  options={options}
                  value={chosen[r.key] || ''}
                  onChange={(v) => setChosen(prev => ({ ...prev, [r.key]: v || '' }))}
                  placeholder="Choose the customer…"
                  disabled={!!r.invoice_block}
                />
                {r.invoice_to?.po_name && (
                  <div className="pc-muted">On the PO: {r.invoice_to.po_name}{r.invoice_to.how === 'postcode' ? ' (found by delivery postcode)' : ''}</div>
                )}
                {block && <div className="pc-flag warn"><span className="material-symbols-rounded">info</span>{block}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {groups.length > 0 && (
        <div className="xpi-groups">
          <b>Will raise:</b>
          {groups.map(g => (
            <span key={g.id} className="xpi-group">{g.name} — {g.lines.length} line{g.lines.length === 1 ? '' : 's'}, {money(g.net)} + VAT</span>
          ))}
        </div>
      )}
    </>
  );
}

export default XeroPoInvoiceModal;
